import React, {useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import pick from 'lodash/pick';
import CommonPageWrapper from '../../../components/CommonPageWrapper';
import {
  Stack,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Divider,
  Badge,
  TablePagination,
  Breadcrumbs,
  Link,
  Skeleton,
} from '@mui/material';
import {ArrowRight} from 'lucide-react';

const styles = {
  table: {
    border: '1px solid #EFF3F1',
  },
  title: {
    marginBottom: '1.3rem !important',
  },
  wrapper: {
    paddingTop: '5rem',
    paddingLeft: '3rem',
    paddingRight: '3rem',
    height: '100%',
  },
  columnHeaders: {
    height: '1rem !important',
    width: 'max-content',
    backgroundColor: '#EFF3F1',
  },
  badge: {
    '& .MuiBadge-badge': {
      marginTop: '0.7rem !important',
      backgroundColor: '#EFF3F1 !important',
      color: '#65706D !important',
    },
  },
  divider: {
    marginLeft: '-3rem !important',
    marginTop: '1.5rem !important',
    marginBottom: '2rem !important',
    width: '110%',
    border: '0.5px solid rgba(0, 0, 0, 0.87) !important',
  },
  pagination: {
    marginLeft: '-3rem !important',
    marginRight: '1rem !important',
    marginBottom: '2rem !important',
    '& p': {
      marginTop: '1rem !important',
    },
  },
};

const SettingsTable = ({
  tableData,
  totalRows,
  tableHeaders,
  pageTitle,
  badgeTitle,
  setPaginationId,
  isFetching,
  isError,
}) => {
  const navigate = useNavigate();
  const [pageHistory, setPageHistory] = useState([0]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);

    if (newPage > page) {
      // forward
      setPageSize(pageSize + 100);
      const paginationId = Object.values(
        pick(tableData[tableData.length - 1], ['account_number', 'id'])
      )[0];
      setPaginationId(paginationId);
      setPageHistory([...pageHistory, paginationId]);
    } else {
      // backward
      setPageSize(pageSize - 100);
      const previousPaginationNumber = pageHistory[pageHistory.length - 2];
      setPaginationId(previousPaginationNumber);
      setPageHistory(pageHistory.slice(0, -1));
    }
  };

  const renderTableHeaders = useMemo(() => {
    return tableHeaders.map((column) => (
      <TableCell
        key={column.field}
        sx={{
          ...styles.columnHeaders,
          width: column.width,
          fontWeight: 'bold',
        }}
      >
        {column.headerName}
      </TableCell>
    ));
  }, [tableHeaders]);

  const renderTableData = useMemo(() => {
    if (isFetching) {
      return tableHeaders.map((_v, idx) => (
        <TableCell key={idx} align="left">
          <Skeleton
            variant={'rounded'}
            width={_v.width}
            sx={{
              display: 'inline-block',
              marginRight: 2,
            }}
          />
        </TableCell>
      ));
    } else if (isError) {
      return (
        <TableRow key="no-data-row">
          <TableCell key="no-data-cell" colSpan={tableHeaders.length}>
            {`No ${badgeTitle} found`}
          </TableCell>
        </TableRow>
      );
    } else {
      return tableData.map((data) => {
        return (
          <TableRow key={data.id}>
            {Object.keys(data).map((key) => {
              const cellValue =
                data[key] === null
                  ? ''
                  : data[key].toString().charAt(0).toUpperCase() +
                    data[key].toString().slice(1);
              switch (key) {
                case 'id':
                case 'total_count':
                case 'pagination_id':
                case 'is_confirmed':
                  return null;
                case 'account_type':
                  return (
                    <TableCell key={key}>
                      {data[key].slice(1).charAt(0).toUpperCase() +
                        data[key].slice(2)}
                    </TableCell>
                  );
                case 'exclude_from_non_gaap':
                  if (data[key] === true) {
                    return <TableCell key={key}>N/A</TableCell>;
                  } else {
                    return <TableCell key={key}>Yes</TableCell>;
                  }
                default:
                  return <TableCell key={key}>{cellValue}</TableCell>;
              }
            })}
          </TableRow>
        );
      });
    }
  }, [tableData, isError, isFetching]);

  return (
    <CommonPageWrapper sx={styles.wrapper}>
      <Stack flex={1} spacing={1}>
        <Breadcrumbs separator={<ArrowRight size={16} />}>
          <Link
            underline="hover"
            key="1"
            color="inherit"
            onClick={() => navigate('/settings/reporting')}
          >
            <Typography variant={'subtitle2'} sx={{cursor: 'pointer'}}>
              Reporting
            </Typography>
          </Link>
          <Typography variant={'subtitle2'}>{pageTitle}</Typography>
        </Breadcrumbs>
        <Typography variant={'h4'} sx={styles.title}>
          {pageTitle}
        </Typography>
        <Stack direction={'row'} spacing={3}>
          <Typography variant={'subtitle2'}>All {badgeTitle}</Typography>
          <Badge
            badgeContent={totalRows}
            max={totalRows}
            sx={styles.badge}
            color="info"
          />
        </Stack>
        <Divider sx={styles.divider} />
        <TableContainer sx={styles.table}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>{renderTableHeaders}</TableRow>
              {renderTableData}
            </TableHead>
          </Table>
        </TableContainer>
        <TablePagination
          sx={styles.pagination}
          rowsPerPageOptions={[100]}
          component="div"
          count={totalRows}
          rowsPerPage={100}
          page={page}
          onPageChange={handleChangePage}
        />
      </Stack>
    </CommonPageWrapper>
  );
};

SettingsTable.propTypes = {
  tableData: PropTypes.array.isRequired,
  totalRows: PropTypes.number.isRequired,
  tableHeaders: PropTypes.array.isRequired,
  pageTitle: PropTypes.string.isRequired,
  badgeTitle: PropTypes.string.isRequired,
  setPaginationId: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
};

export default SettingsTable;
